import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { removeFromCartList } from "../redux/slices/cartSlice";
import {
  addOrderThunk,
  deleteAddressThunk,
  deleteFromCartThunk,
  fetchAddressesThunk,
  fetchCartItemsThunk,
  fetchProductByIdThunk,
  removeAddressThunk,
} from "../thunks/thunks";
import AddAddressForm from "./AddAddress";
import { toast } from "react-toastify";

const CartPage = () => {
  const navigate = useNavigate();
  const { addresses } = useSelector((state) => state.cart);
  const [currentStep, setCurrentStep] = useState(1);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const { cartList, productDetails } = useSelector((state) => state.cart);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const totalMRP = cartList.reduce(
    (total, item) => total + (productDetails[item.productId]?.price || 0),
    0
  );
  const discount = 100;
  const platformFee = 20;
  const shippingFee = 30;
  const totalAmount = totalMRP - discount + platformFee + shippingFee;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchCartItemsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (currentStep === 2) {
      dispatch(fetchAddressesThunk());
    }
  }, [currentStep, dispatch]);

  const handleNextStep = () => setCurrentStep((prevStep) => prevStep + 1);

  useEffect(() => {
    cartList.forEach((item) => {
      if (!productDetails[item.productId]) {
        dispatch(fetchProductByIdThunk(item.productId));
      }
    });
  }, [cartList, productDetails, dispatch]);

  const handleRemoveAddress = (addressId) => {
    dispatch(removeAddressThunk(addressId));
  };

  const handlePayment = async () => {
    const formattedCartList = {
      products: cartList.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
      })),
    };

    if(!selectedAddress){
      toast.error("Please select an address to proceed!");
      return;
    }
    await dispatch(addOrderThunk({
      products:formattedCartList.products,
      address:selectedAddress
    }));
    navigate("/");
  };

  const handleAddressAdded = async () => {
    await dispatch(fetchAddressesThunk());
    setIsAddingAddress(false);
    toast.success("Addresses fetched successfully!");
    
    setCurrentStep(2);
  };

  return (
    <div className="min-h-screen bg-custom-pink">
      {/* Navbar */}
      <nav className="flex items-center justify-between px-4 sm:px-10 py-4 bg-white shadow-md">
        <Link to="/main">
          <div className="w-[5rem] sm:w-[6rem]">
            <img src="/assets/logoSvg.svg" alt="Logo" />
          </div>
        </Link>
        <h2 className="text-xl sm:text-2xl font-semibold text-black bg-gray-200 p-2 rounded-md shadow-md">
          {`Step ${currentStep}: ${
            ["Cart Summary", "Address Confirmation", "Payment"][currentStep - 1]
          }`}
        </h2>
        <div className="text-green-500 hidden sm:block">
          <FontAwesomeIcon icon={faLock} /> 100% Secure
        </div>
      </nav>

      <div className="flex flex-col sm:flex-row justify-center gap-6 sm:gap-16 mt-8 mx-4 w-[90%]">
        {/* Step-Specific Content */}
        <div className="w-full sm:w-[70%] p-6 bg-white shadow-lg rounded-lg ">
          {currentStep === 1 && (
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold mb-4 shadow-md w-[fit-content] p-2 bg-gray-200 rounded-md">
                Shopping Cart
              </h3>
              {cartList.length === 0 ? (
                <p>Your cart is empty</p>
              ) : (
                cartList.map((item) => {
                  const product = productDetails[item.productId];
                  return product ? (
                    <div
                      key={item.productId}
                      className="flex flex-col sm:flex-row border-b py-4"
                    >
                      <div className="w-full sm:w-[40%] mb-4 sm:mb-0">
                        <img
                          src={product?.imageFileName}
                          alt={product?.name}
                          className="w-full sm:w-40 object-cover rounded-md shadow-md"
                        />
                      </div>
                      <div className="text-left w-full sm:w-[55%]">
                        <p className="text-2xl font-bold">{product?.name}</p>
                        <p className="text-1xl font-bold text-gray-500">
                          Size: {item.size}
                        </p>
                        <p className="text-1xl font-bold text-gray-500">
                          Qty: {item.quantity}
                        </p>
                        <p className="text-1xl font-bold">
                          Price: ${product?.price}
                        </p>
                      </div>
                      <div className="mt-2 sm:mt-0 flex justify-end h-[fit-content]">
                        <button
                          className="text-red-500 w-6 sm:w-8 cursor-pointer"
                          onClick={() => {
                            dispatch(
                              deleteFromCartThunk({ productId: item.productId }),
                              setTimeout(() => {
                                window.location.reload()
                              }
                              , 1000)
                            );
                            toast.success(
                              "Product removed from cart successfully!"
                            );
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} className="mr-2" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p key={item.productId}>Loading product details...</p>
                  );
                })
              )}
              {cartList.length > 0 && (
                <button
                  onClick={handleNextStep}
                  className="mt-6 w-full sm:w-auto bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
                >
                  Proceed to Address
                </button>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold mb-4 bg-gray-200 rounded-md shadow-md w-[fit-content] p-2">
                Address Confirmation
              </h3>
              <div className="space-y-4">
                <div>
                  <h3 className="text-1xl">Select an Address:</h3>
                  {addresses.length === 0 ? (
                    <p>No addresses found</p>
                  ) : (
                    <ul>
                      {addresses.map((address, index) => (
                        <li key={index} className="flex justify-between my-2">
                          <label>
                            <input
                              type="radio"
                              name="address"
                              value={address.street}
                              onChange={() => setSelectedAddress(address)}
                            />
                            {`${address.street}, ${address.city}, ${address.state}, ${address.postalCode}`}
                          </label>
                          <button
                            onClick={() =>
                              {
                                dispatch(deleteAddressThunk(address._id))
                                toast.success("Address deleted successfully!")
                                setTimeout(() => {
                                  window.location.reload()
                                  setCurrentStep(2);
                                }
                                , 1000)
                                
                              }
                            }
                            className="text-red-500 hover:text-red-700"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {!isAddingAddress ? (
                  <button
                    onClick={() => {
                      if (addresses.length < 3) {
                        setIsAddingAddress(true);
                      } else {
                        toast.error("You can add a maximum of 3 addresses!");
                      }
                    }}
                    className="text-blue-500 hover:underline"
                  >
                    + Add New Address
                  </button>
                ) : (
                  <AddAddressForm onAddressAdded={handleAddressAdded} />
                )}
              </div>
              <button
                onClick={handleNextStep}
                className="mt-6 w-full sm:w-auto bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
              >
                Proceed to Payment
              </button>
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold mb-4">
                Payment Options
              </h3>
              <button
                onClick={handlePayment}
                className="mt-6 w-full sm:w-auto bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600"
              >
                Pay ₹{totalAmount}
              </button>
            </div>
          )}
        </div>

        {/* Order Summary Column */}
        <div className="w-full sm:w-[50%] p-6 bg-white shadow-lg rounded-lg h-[fit-content] mb-4">
          <h3 className="text-xl sm:text-2xl font-semibold mb-4">
            Order Summary
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <p>MRP Total</p>
              <p>${totalMRP}</p>
            </div>
            <div className="flex justify-between">
              <p>Discount</p>
              <p className="text-green-500">- ${discount}</p>
            </div>
            <div className="flex justify-between">
              <p>Platform Fee</p>
              <p>${platformFee}</p>
            </div>
            <div className="flex justify-between">
              <p>Shipping Fee</p>
              <p>${shippingFee}</p>
            </div>
            <div className="flex justify-between border-t pt-2 font-semibold">
              <p>Total</p>
              <p>${totalAmount}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
